import type { DocPage } from '../types'
import { media } from '../media'


export const connectKeys: DocPage = {
  id: 'connect-keys',
  title: 'Connect keys',
  description:
    'Configure Connect-ID and Connect-Delete keys, predicate expressions (1 OR 2), create-on-no-match globally and per collection, and Disconnect vs regular Connect.',
  category: 'Mapping',
  sections: [
    { id: 'overview', title: 'Overview' },
    { id: 'mark', title: 'How to mark keys' },
    { id: 'predicate', title: 'Predicate expression' },
    { id: 'create', title: 'Create-on-no-match' },
    { id: 'delete', title: 'Connect-Delete' },
    { id: 'disconnect', title: 'Disconnect' },
    { id: 'regular', title: 'Regular Connect vs keys' },
    { id: 'workflow', title: 'Update workflow' },
  ],
  blocks: [
    {
      type: 'heading',
      level: 2,
      id: 'overview',
      text: 'Overview',
    },
    {
      type: 'paragraph',
      text: 'Connect-ID and Connect-Delete turn ordinary attribute mappings into match keys. Connect-ID finds existing target rows to update. Connect-Delete finds target rows to remove. Availability depends on the formats in use; both appear in the vertical rail tooltip group: Connect-ID, Connect-Delete, and Disconnect.',
    },
    {
      type: 'figure',
      src: media.connectIdAndDelete,
      alt: 'Connect-ID options with predicate and create-on-no-match',
      caption:
        'Figure: Mapping tree showing Connect-ID (blue key) and Connect-Delete (red trash) badges on attribute mappings, plus collection-level Connect-ID / Connect-Delete counts.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'mark',
      text: 'How to mark keys',
    },
    {
      type: 'steps',
      items: [
        'Create regular attribute mappings with Connect for the fields that identify a row (for example asset_id, uuid).',
        'Select those attribute mappings.',
        'Click Connect-ID — map attributes as update keys (match existing target rows), or Connect-Delete — map attributes as delete keys (remove matching target rows).',
        'Configure the Predicate expression when multiple keys are marked.',
        'Set create-on-no-match behavior for Connect-ID (global and optional per-collection overrides).',
      ],
    },
    {
      type: 'paragraph',
      text: 'The mapping tree summarizes keys, for example Connect-ID: {predicate} · create on no match, plus the Connect-Delete predicate when delete keys are active.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'predicate',
      text: 'Predicate expression',
    },
    {
      type: 'paragraph',
      text: 'When more than one attribute is marked as a key, open Predicate expression. Keys are referenced by 1-based indices in the current attribute-mapping order (the numbers shown as Connect-ID #n / Connect-Delete #n in the tree). Drag mappings to change that order. The default combination is OR.',
    },
    {
      type: 'table',
      monoFirst: true,
      headers: ['Example', 'Meaning'],
      rows: [
        ['1 OR 2', 'Match if key 1 or key 2 matches (typical two-key OR).'],
        ['(1 OR 2) AND 3', 'Match if (key1 or key2) and key3 all satisfy the match logic.'],
      ],
    },
    {
      type: 'list',
      items: [
        'Use parentheses for precedence.',
        'Save or Cancel from the predicate editor.',
        'Keep predicates simple and document which index is which attribute in your run notes.',
        'Reordering Connect-ID or Connect-Delete attribute rows changes which field is 1, 2, 3 in a custom predicate unless you edit the expression afterwards.',
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'create',
      text: 'Create-on-no-match',
    },
    {
      type: 'heading',
      level: 3,
      id: 'create-global',
      text: 'Global default',
    },
    {
      type: 'paragraph',
      text: 'Label: Create new features when Connect-ID does not match any target row (global default). Toggle On/Off from the mapping toolbar key control. The control stays disabled until at least one Connect-ID mapping exists.',
    },
    {
      type: 'heading',
      level: 3,
      id: 'create-per-collection',
      text: 'Per collection',
    },
    {
      type: 'table',
      headers: ['Option', 'Behavior'],
      rows: [
        [
          'When no target row matches → Use global default ({{global}})',
          'Inherit the global On/Off setting.',
        ],
        ['Create new feature', 'Insert when no match, regardless of global Off.'],
        ['Skip feature', 'Do not insert when no match, regardless of global On.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'delete',
      text: 'Connect-Delete',
    },
    {
      type: 'paragraph',
      text: 'Connect-Delete marks keys used to delete matching target rows. Configure its predicate the same way as Connect-ID. Treat delete keys carefully — verify against a small filtered subset or test database before a production run.',
    },
    {
      type: 'callout',
      variant: 'warning',
      title: 'Destructive operation',
      text: 'Connect-Delete removes matching target rows. Confirm predicates, inclusion filters, and Map layer visibility before Transfer.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'disconnect',
      text: 'Disconnect',
    },
    {
      type: 'paragraph',
      text: 'Disconnect removes the selected mapping link (collection or attribute), including key marking associated with that link. It does not delete target database rows — it only clears the mapping definition in the workspace.',
    },
    {
      type: 'heading',
      level: 2,
      id: 'regular',
      text: 'Regular Connect vs keys',
    },
    {
      type: 'table',
      headers: ['Action', 'Result'],
      rows: [
        ['Connect', 'Defines how source values copy/transform into target attributes.'],
        ['Connect-ID', 'Uses mapped attributes as identity for update matching.'],
        ['Connect-Delete', 'Uses mapped attributes as identity for delete matching.'],
        ['Disconnect', 'Removes the mapping (and key role) from the panel.'],
      ],
    },
    {
      type: 'heading',
      level: 2,
      id: 'workflow',
      text: 'Update workflow',
    },
    {
      type: 'steps',
      items: [
        'Build attribute mappings first (manually or with Auto Map).',
        'Mark identity attributes as Connect-ID.',
        'Set Predicate expression (for example 1 OR 2).',
        'Set global create-on-no-match, then per-collection overrides if needed.',
        'Optionally configure Connect-Delete on a separate controlled run.',
        'Run Transfer and verify updated/inserted/deleted counts in the logger and Transfer History.',
      ],
    },
  ],
}
